import React, { useState } from 'react';
import HypothesisDeckPreviewView from './HypothesisDeckPreviewView.js';
import HypothesisDeckEditorView from './HypothesisDeckEditorView.js';
import { getUILoc } from '../../utils/cardUtils.js';
import audioManager from '../../utils/AudioManager.js';
import './HypothesisVerificationScreen.css';

const HypothesisVerificationScreen = ({
  gameData,
  config,
  onUpdateConfig,
  onStartExperiment,
  onBackToTitle
}) => {
  const [view, setView] = useState('config'); // 'config' | 'deckPreview' | 'deckEdit'
  const [targetPlayer, setTargetPlayer] = useState('player'); // 'player' | 'npc'
  const [showExitConfirm, setShowExitConfirm] = useState(false);

  const { cardDefs } = gameData;

  // デッキプレビューへ遷移
  const handleOpenPreview = (playerType) => {
    audioManager.playSE('select');
    setTargetPlayer(playerType);
    setView('deckPreview');
  };

  // デッキ編集へ遷移
  const handleOpenEditor = (playerType) => {
    audioManager.playSE('select');
    setTargetPlayer(playerType);
    setView('deckEdit');
  };

  // デッキ編集完了時
  const handleSaveDeck = (newDeckCards) => {
    if (targetPlayer === 'player') {
      onUpdateConfig({ ...config, playerDeck: newDeckCards });
    } else {
      onUpdateConfig({ ...config, npcDeck: newDeckCards });
    }
    setView('config');
  };

  // デッキのリセット
  const handleResetDeck = (playerType) => {
    audioManager.playSE('cancel');
    if (playerType === 'player') {
      onUpdateConfig({ ...config, playerDeck: [] });
    } else {
      onUpdateConfig({ ...config, npcDeck: [] });
    }
  };

  // パラメータ更新ヘルパー
  const handleParamChange = (field, delta, min = 1, max = 999) => {
    audioManager.playSE('select');
    const currentVal = config[field] ?? min;
    const newVal = Math.max(min, Math.min(max, currentVal + delta));
    onUpdateConfig({ ...config, [field]: newVal });
  };

  // 実証（対局開始）
  const handleStart = () => {
    const isPlayerDeckEmpty = (!config.playerDeck || config.playerDeck.length === 0) && !config.playerAntiDeckOut;
    const isNpcDeckEmpty = (!config.npcDeck || config.npcDeck.length === 0) && !config.npcAntiDeckOut;

    if (isPlayerDeckEmpty || isNpcDeckEmpty) {
      audioManager.playSE('cancel');
      alert(getUILoc('MESSAGES.HYPOTHESIS_DECK_EMPTY_ALERT'));
      return;
    }

    audioManager.playSE('select');
    onStartExperiment();
  };

  // タイトルへ戻る処理（確認付き）
  const handleBackClicked = () => {
    audioManager.playSE('cancel');
    setShowExitConfirm(true);
  };

  const handleConfirmExit = () => {
    audioManager.playSE('select');
    setShowExitConfirm(false);
    onBackToTitle();
  };

  const handleCancelExit = () => {
    audioManager.playSE('cancel');
    setShowExitConfirm(false);
  };

  // デッキプレビュー表示
  if (view === 'deckPreview') {
    const isPlayer = targetPlayer === 'player';
    const activeDeck = isPlayer ? config.playerDeck : config.npcDeck;
    const title = isPlayer 
      ? `${getUILoc('LABELS.PLAYER_SETTINGS')} - ${getUILoc('BUTTONS.CHECK_DECK')}`
      : `${getUILoc('LABELS.OPPONENT_SETTINGS')} - ${getUILoc('BUTTONS.CHECK_DECK')}`;

    return (
      <HypothesisDeckPreviewView
        deckCards={activeDeck}
        cardDefs={cardDefs}
        title={title}
        onBack={() => setView('config')}
      />
    );
  }

  // デッキ編集表示
  if (view === 'deckEdit') {
    const isPlayer = targetPlayer === 'player';
    const activeDeck = isPlayer ? config.playerDeck : config.npcDeck;
    const title = isPlayer 
      ? `${getUILoc('LABELS.PLAYER_SETTINGS')} - ${getUILoc('BUTTONS.EDIT_DECK')}`
      : `${getUILoc('LABELS.OPPONENT_SETTINGS')} - ${getUILoc('BUTTONS.EDIT_DECK')}`;

    return (
      <HypothesisDeckEditorView
        initialDeckCards={activeDeck}
        cardDefs={cardDefs}
        title={title}
        onSaveAndBack={handleSaveDeck}
      />
    );
  }

  return (
    <div className="hypothesis-verification-screen">
      {/* 退出確認モーダル */}
      {showExitConfirm && (
        <div className="choice-prompt-overlay" onClick={handleCancelExit}>
          <div className="choice-prompt" onClick={(e) => e.stopPropagation()}>
            <h3>{getUILoc('MESSAGES.EXIT_HYPOTHESIS_CONFIRM')}</h3>
            <p>{getUILoc('MESSAGES.EXIT_HYPOTHESIS_RESET_WARNING')}</p>
            <div className="choice-options">
              <button className="surrender-confirm-yes" onClick={handleConfirmExit}>
                {getUILoc('BUTTONS.YES')}
              </button>
              <button onClick={handleCancelExit}>
                {getUILoc('BUTTONS.NO')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ヘッダー */}
      <div className="hypothesis-header">
        <h1>{getUILoc('BUTTONS.HYPOTHESIS_VERIFICATION')} - {getUILoc('LABELS.HYPOTHESIS_MATCH_SETTINGS')}</h1>
        <button className="back-button" onClick={handleBackClicked}>
          ← {getUILoc('BUTTONS.BACK')}
        </button>
      </div>

      <div className="hypothesis-content-container">
        {/* プレイヤー & 相手設定カードグリッド */}
        <div className="hypothesis-players-grid">
          {/* プレイヤー（自分）設定 */}
          <div className="player-config-card">
            <h2 className="card-section-title">
              <span>👤</span> {getUILoc('LABELS.PLAYER_SETTINGS')}
            </h2>

            {/* デッキ設定 */}
            <div className="deck-config-box">
              <div className="deck-status-row">
                <span className="deck-count-badge">
                  {getUILoc('LABELS.CURRENT_DECK_COUNT', { count: config.playerDeck?.length || 0 })}
                </span>
              </div>
              <div className="deck-button-group">
                <button className="deck-btn" onClick={() => handleOpenPreview('player')}>
                  {getUILoc('BUTTONS.CHECK_DECK')}
                </button>
                <button className="deck-btn edit-btn" onClick={() => handleOpenEditor('player')}>
                  {getUILoc('BUTTONS.EDIT_DECK')}
                </button>
                <button className="deck-btn reset-btn" onClick={() => handleResetDeck('player')}>
                  {getUILoc('BUTTONS.RESET_DECK')}
                </button>
              </div>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={config.playerAntiDeckOut}
                  onChange={(e) => onUpdateConfig({ ...config, playerAntiDeckOut: e.target.checked })}
                />
                <span>{getUILoc('LABELS.ANTI_DECK_OUT')} ({getUILoc('LABELS.ANTI_DECK_OUT_DESC')})</span>
              </label>
            </div>

            {/* 初期パラメータ設定 */}
            <div className="param-setting-row">
              <span className="param-label">{getUILoc('LABELS.CONSCIOUSNESS')}</span>
              <div className="param-stepper">
                <button 
                  className="param-btn" 
                  disabled={config.playerConsciousness <= 1}
                  onClick={() => handleParamChange('playerConsciousness', -1, 1)}
                >
                  -
                </button>
                <span className="param-value-input">{config.playerConsciousness}</span>
                <button 
                  className="param-btn" 
                  onClick={() => handleParamChange('playerConsciousness', 1, 1)}
                >
                  +
                </button>
              </div>
            </div>

            <div className="param-setting-row">
              <span className="param-label">{getUILoc('LABELS.SCALE')}</span>
              <div className="param-stepper">
                <button 
                  className="param-btn" 
                  disabled={config.playerScale <= 1}
                  onClick={() => handleParamChange('playerScale', -1, 1)}
                >
                  -
                </button>
                <span className="param-value-input">{config.playerScale}</span>
                <button 
                  className="param-btn" 
                  onClick={() => handleParamChange('playerScale', 1, 1)}
                >
                  +
                </button>
              </div>
            </div>

            <div className="param-setting-row">
              <span className="param-label">{getUILoc('LABELS.INITIAL_HAND_SIZE')}</span>
              <div className="param-stepper">
                <button 
                  className="param-btn" 
                  disabled={config.playerInitialHandSize <= 0}
                  onClick={() => handleParamChange('playerInitialHandSize', -1, 0, 10)}
                >
                  -
                </button>
                <span className="param-value-input">{config.playerInitialHandSize}</span>
                <button 
                  className="param-btn" 
                  disabled={config.playerInitialHandSize >= 10}
                  onClick={() => handleParamChange('playerInitialHandSize', 1, 0, 10)}
                >
                  +
                </button>
              </div>
            </div>
          </div>

          {/* 相手（NPC）設定 */}
          <div className="player-config-card opponent-card">
            <h2 className="card-section-title">
              <span>🤖</span> {getUILoc('LABELS.OPPONENT_SETTINGS')}
            </h2>

            {/* デッキ設定 */}
            <div className="deck-config-box">
              <div className="deck-status-row">
                <span className="deck-count-badge">
                  {getUILoc('LABELS.CURRENT_DECK_COUNT', { count: config.npcDeck?.length || 0 })}
                </span>
              </div>
              <div className="deck-button-group">
                <button className="deck-btn" onClick={() => handleOpenPreview('npc')}>
                  {getUILoc('BUTTONS.CHECK_DECK')}
                </button>
                <button className="deck-btn edit-btn" onClick={() => handleOpenEditor('npc')}>
                  {getUILoc('BUTTONS.EDIT_DECK')}
                </button>
                <button className="deck-btn reset-btn" onClick={() => handleResetDeck('npc')}>
                  {getUILoc('BUTTONS.RESET_DECK')}
                </button>
              </div>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={config.npcAntiDeckOut}
                  onChange={(e) => onUpdateConfig({ ...config, npcAntiDeckOut: e.target.checked })}
                />
                <span>{getUILoc('LABELS.ANTI_DECK_OUT')} ({getUILoc('LABELS.ANTI_DECK_OUT_DESC')})</span>
              </label>
            </div>

            {/* 初期パラメータ設定 */}
            <div className="param-setting-row">
              <span className="param-label">{getUILoc('LABELS.CONSCIOUSNESS')}</span>
              <div className="param-stepper">
                <button 
                  className="param-btn" 
                  disabled={config.npcConsciousness <= 1}
                  onClick={() => handleParamChange('npcConsciousness', -1, 1)}
                >
                  -
                </button>
                <span className="param-value-input">{config.npcConsciousness}</span>
                <button 
                  className="param-btn" 
                  onClick={() => handleParamChange('npcConsciousness', 1, 1)}
                >
                  +
                </button>
              </div>
            </div>

            <div className="param-setting-row">
              <span className="param-label">{getUILoc('LABELS.SCALE')}</span>
              <div className="param-stepper">
                <button 
                  className="param-btn" 
                  disabled={config.npcScale <= 1}
                  onClick={() => handleParamChange('npcScale', -1, 1)}
                >
                  -
                </button>
                <span className="param-value-input">{config.npcScale}</span>
                <button 
                  className="param-btn" 
                  onClick={() => handleParamChange('npcScale', 1, 1)}
                >
                  +
                </button>
              </div>
            </div>

            <div className="param-setting-row">
              <span className="param-label">{getUILoc('LABELS.INITIAL_HAND_SIZE')}</span>
              <div className="param-stepper">
                <button 
                  className="param-btn" 
                  disabled={config.npcInitialHandSize <= 0}
                  onClick={() => handleParamChange('npcInitialHandSize', -1, 0, 10)}
                >
                  -
                </button>
                <span className="param-value-input">{config.npcInitialHandSize}</span>
                <button 
                  className="param-btn" 
                  disabled={config.npcInitialHandSize >= 10}
                  onClick={() => handleParamChange('npcInitialHandSize', 1, 0, 10)}
                >
                  +
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* 先攻設定 */}
        <div className="common-settings-card">
          <span style={{ fontWeight: 'bold', fontSize: '1rem', color: '#cbd5e1' }}>
            {getUILoc('LABELS.FIRST_PLAYER_SETTING')}
          </span>
          <div className="order-selector-group">
            <button
              className={`order-radio-btn ${config.turnOrder === 'player' ? 'active' : ''}`}
              onClick={() => {
                audioManager.playSE('select');
                onUpdateConfig({ ...config, turnOrder: 'player' });
              }}
            >
              {getUILoc('LABELS.FIRST_PLAYER_HUMAN')}
            </button>
            <button
              className={`order-radio-btn ${config.turnOrder === 'npc' ? 'active' : ''}`}
              onClick={() => {
                audioManager.playSE('select');
                onUpdateConfig({ ...config, turnOrder: 'npc' });
              }}
            >
              {getUILoc('LABELS.FIRST_PLAYER_NPC')}
            </button>
            <button
              className={`order-radio-btn ${config.turnOrder === 'random' ? 'active' : ''}`}
              onClick={() => {
                audioManager.playSE('select');
                onUpdateConfig({ ...config, turnOrder: 'random' });
              }}
            >
              {getUILoc('LABELS.FIRST_PLAYER_RANDOM')}
            </button>
          </div>
        </div>

        {/* 下部アクション */}
        <div className="hypothesis-bottom-actions">
          <button className="back-button" onClick={handleBackClicked}>
            {getUILoc('BUTTONS.BACK')}
          </button>
          <button className="experiment-start-btn" onClick={handleStart}>
            {getUILoc('BUTTONS.EXPERIMENT')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HypothesisVerificationScreen;
